import { Controller } from "@hotwired/stimulus";

// Modal Controller
// Manages modal dialog state with event-based control and animation.
//
// Required values:
//   data-crud--modal-id-value: Unique identifier for this modal instance
//
// Optional values:
//   data-crud--modal-prevent-dismiss-value: Prevents closing via ESC/backdrop (default: false)
//
// Events:
//   "modal:open" with detail: modalId - Opens the modal if ID matches
//   "modal:close" - Closes any open modal
//
// Usage:
//   <dialog data-controller="crud--modal"
//           data-crud--modal-id-value="my-modal"
//           data-crud--modal-target="dialog">
//     <!-- modal content -->
//   </dialog>
//
export default class extends Controller {
  static values = {
    id: { type: String },
    preventDismiss: { type: Boolean, default: false },
  };

  static targets = ["dialog"];

  connect() {
    if (!this.idValue) { throw new Error("Modal controller requires an explicit ID"); }

    this.boundCloseOnEscape = this.closeOnEscape.bind(this);
    this.boundCloseOnTurboBeforeCache = this.closeOnTurboBeforeCache.bind(this);
    this.boundCloseOnEvent = this.closeOnEvent.bind(this);
    this.boundOpenOnEvent = this.openOnEvent.bind(this);

    document.addEventListener("keydown", this.boundCloseOnEscape);
    document.addEventListener("turbo:before-cache", this.boundCloseOnTurboBeforeCache);
    document.addEventListener("modal:close", this.boundCloseOnEvent);
    document.addEventListener("modal:open", this.boundOpenOnEvent);
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundCloseOnEscape);
    document.removeEventListener("turbo:before-cache", this.boundCloseOnTurboBeforeCache);
    document.removeEventListener("modal:close", this.boundCloseOnEvent);
    document.removeEventListener("modal:open", this.boundOpenOnEvent);
    
    if (this.dialogTarget.open) { this.close(); }
  }

  close(event) {
    if (event) { event.preventDefault(); }

    this.animateOut().then(() => {
      this.dialogTarget.close();
      this.unlockBodyScroll();
    });
  }

  backdropClose(event) {
    if (event.target === this.dialogTarget) { this.closeIfAllowed(); }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") { this.closeIfAllowed(); }
  }

  closeOnEvent() {
    this.closeIfAllowed();
  }

  open(event) {
    if (event?.preventDefault) { event.preventDefault(); }

    this.dialogTarget.showModal();
    this.lockBodyScroll();
    this.animateIn();
  }

  openOnEvent(event) {
    const modalId = event.detail;
    
    // Filter by modal ID - only open if it matches this modal's ID
    if (modalId !== this.idValue) { return; }
    
    this.open();
  }

  closeIfAllowed() {
    if (this.dialogTarget.open && !this.preventDismissValue) { this.close(); }
  }

  closeOnTurboBeforeCache() {
    if (this.dialogTarget.open) {
      this.dialogTarget.close();
      this.unlockBodyScroll();
    }
  }

  animateIn() {
    const dialog = this.dialogTarget;
    dialog.classList.remove("opacity-0", "scale-95");
    dialog.classList.add("opacity-100", "scale-100");
  }

  animateOut() {
    const dialog = this.dialogTarget;
    dialog.classList.remove("opacity-100", "scale-100");
    dialog.classList.add("opacity-0", "scale-95");
    
    return new Promise((resolve) => {
      setTimeout(resolve, 150); // Match transition duration
    });
  }

  lockBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  unlockBodyScroll() {
    document.body.style.overflow = "";
  }
}

// Exported for use as Turbo Stream actions, to be registered in turbo_stream_actions/index.js

export function openModal() {
  document.dispatchEvent(new CustomEvent("modal:open", { detail: this.target, bubbles: true }));
}

// This does not take a modal ID because only one modal can ever be open at a time
export function closeModal() {
  document.dispatchEvent(new CustomEvent("modal:close", { bubbles: true }));
}
